/**
 * LightingManager - Handles all scene lighting
 *
 * Manages ambient light, directional (sun) light, and headlights.
 * Colors and intensities change during day/night transitions.
 *
 * Responsibilities:
 * - Create and configure ambient and directional lights
 * - Create and position headlights (attached to camera)
 * - Update light colors/intensities during day/night cycle
 * - Toggle headlight visibility based on time of day
 */

import * as THREE from 'three';
import { LIGHTING, RENDERING } from '../config/constants.js';

export class LightingManager {
    /**
     * Create a new LightingManager
     * @param {THREE.Scene} scene - The Three.js scene to add lights to
     * @param {THREE.Camera} camera - The camera (for attaching headlights)
     * @param {ScenePreloader} [preloader] - Optional preloader for progress tracking
     */
    constructor(scene, camera, preloader = null) {
        this.scene = scene;
        this.camera = camera;

        // Self-register for preload tracking with dispose callback
        this._onPreloadComplete = preloader?.register('lighting', {
            dispose: () => this.dispose()
        });

        // Light references
        this.ambientLight = null;
        this.directionalLight = null;
        this.headlightLeft = null;
        this.headlightRight = null;

        // Current values (will be updated by color cycle)
        this.ambientColor = LIGHTING.AMBIENT.DEFAULT_COLOR;
        this.ambientIntensity = LIGHTING.AMBIENT.DEFAULT_INTENSITY;
        this.directionalColor = LIGHTING.DIRECTIONAL.DEFAULT_COLOR;
        this.directionalIntensity = LIGHTING.DIRECTIONAL.DEFAULT_INTENSITY;

        // Headlight configuration (from constants)
        this.headlightsEnabled = LIGHTING.HEADLIGHTS.ENABLED;
        this.headlightBrightness = LIGHTING.HEADLIGHTS.BRIGHTNESS;
        this.headlightDistance = LIGHTING.HEADLIGHTS.DISTANCE;
        this.headlightAngle = LIGHTING.HEADLIGHTS.ANGLE;
        this.headlightAutoNightOnly = LIGHTING.HEADLIGHTS.AUTO_NIGHT_ONLY;
        this.headlightThreshold = LIGHTING.HEADLIGHTS.NIGHT_THRESHOLD;
        this.headlightSpread = LIGHTING.HEADLIGHTS.SPREAD;
        this.headlightOffsetY = LIGHTING.HEADLIGHTS.OFFSET_Y;
        this.headlightOffsetZ = LIGHTING.HEADLIGHTS.OFFSET_Z;
    }

    /**
     * Initialize all lights
     * Call this after scene and camera are ready
     */
    initialize() {
        this.createAmbientLight();
        this.createDirectionalLight();
        this.createHeadlights();
        this._onPreloadComplete?.();
    }

    /**
     * Create ambient light
     * @private
     */
    createAmbientLight() {
        this.ambientLight = new THREE.AmbientLight(this.ambientColor, this.ambientIntensity);
        this.scene.add(this.ambientLight);
    }

    /**
     * Create directional (sun) light with shadows
     * @private
     */
    createDirectionalLight() {
        this.directionalLight = new THREE.DirectionalLight(this.directionalColor, this.directionalIntensity);
        this.directionalLight.castShadow = true;

        // Shadow camera configuration - large frustum for terrain shadows
        const shadowCam = this.directionalLight.shadow.camera;
        shadowCam.left = -RENDERING.SHADOW_CAMERA_SIZE;
        shadowCam.right = RENDERING.SHADOW_CAMERA_SIZE;
        shadowCam.top = RENDERING.SHADOW_CAMERA_SIZE;
        shadowCam.bottom = -RENDERING.SHADOW_CAMERA_SIZE;
        shadowCam.near = RENDERING.SHADOW_CAMERA_NEAR || 1;
        shadowCam.far = RENDERING.SHADOW_CAMERA_FAR || 1500;

        this.directionalLight.shadow.mapSize.width = RENDERING.SHADOW_MAP_SIZE;
        this.directionalLight.shadow.mapSize.height = RENDERING.SHADOW_MAP_SIZE;

        // Shadow bias to prevent shadow acne
        // bias: small negative value, normalBias: keep below 0.5 per best practices
        this.directionalLight.shadow.bias = -0.001;
        this.directionalLight.shadow.normalBias = 0.4;

        // Create target for directional light (will follow camera)
        this.directionalLightTarget = new THREE.Object3D();
        this.scene.add(this.directionalLightTarget);
        this.directionalLight.target = this.directionalLightTarget;

        this.scene.add(this.directionalLight);
    }

    /**
     * Update directional light position based on sun direction and camera position
     * Call this each frame to make shadows follow the player
     * @param {THREE.Vector3} cameraPosition - Current camera position
     * @param {number} sunArcProgress - Sun position on arc (0 = sunrise, 0.5 = noon, 1 = sunset)
     */
    updateSunPosition(cameraPosition, sunArcProgress) {
        if (!this.directionalLight) return;

        // Calculate sun direction from arc progress
        // Arc goes from behind (+Z) through overhead to front (-Z)
        const sunAngle = Math.PI * sunArcProgress;

        // Sun direction vector (normalized)
        const sunHeight = Math.sin(sunAngle);
        const sunDepth = Math.cos(sunAngle);

        // Position light relative to camera, offset in sun direction
        // Light needs to be far enough to cast shadows across terrain
        const lightDistance = 500;

        this.directionalLight.position.set(
            cameraPosition.x,
            cameraPosition.y + sunHeight * lightDistance,
            cameraPosition.z + sunDepth * lightDistance
        );

        // Target follows camera
        this.directionalLightTarget.position.copy(cameraPosition);

        // Update shadow camera
        this.directionalLight.shadow.camera.updateProjectionMatrix();
    }

    /**
     * Create headlights (attached to camera for night driving)
     * @private
     */
    createHeadlights() {
        // Left headlight
        this.headlightLeft = new THREE.SpotLight(
            0xffffff,
            this.headlightBrightness,
            this.headlightDistance,
            this.headlightAngle,
            LIGHTING.HEADLIGHTS.PENUMBRA,
            LIGHTING.HEADLIGHTS.DECAY
        );
        this.headlightLeft.position.set(
            -this.headlightSpread,
            this.headlightOffsetY,
            this.headlightOffsetZ
        );
        this.headlightLeft.castShadow = true;
        this.camera.add(this.headlightLeft);
        this.camera.add(this.headlightLeft.target);
        this.headlightLeft.target.position.set(
            -this.headlightSpread,
            this.headlightOffsetY,
            this.headlightOffsetZ + LIGHTING.HEADLIGHTS.TARGET_OFFSET_Z
        );

        // Right headlight
        this.headlightRight = new THREE.SpotLight(
            0xffffff,
            this.headlightBrightness,
            this.headlightDistance,
            this.headlightAngle,
            LIGHTING.HEADLIGHTS.PENUMBRA,
            LIGHTING.HEADLIGHTS.DECAY
        );
        this.headlightRight.position.set(
            this.headlightSpread,
            this.headlightOffsetY,
            this.headlightOffsetZ
        );
        this.headlightRight.castShadow = true;
        this.camera.add(this.headlightRight);
        this.camera.add(this.headlightRight.target);
        this.headlightRight.target.position.set(
            this.headlightSpread,
            this.headlightOffsetY,
            this.headlightOffsetZ + LIGHTING.HEADLIGHTS.TARGET_OFFSET_Z
        );

        // Set initial visibility
        this.updateHeadlightVisibility();
    }

    /**
     * Set ambient light color and intensity
     * @param {number} color - Hex color
     * @param {number} intensity - Light intensity (0-1)
     */
    setAmbient(color, intensity) {
        this.ambientColor = color;
        this.ambientIntensity = intensity;
        if (this.ambientLight) {
            this.ambientLight.color.setHex(color);
            this.ambientLight.intensity = intensity;
        }
    }

    /**
     * Set directional light color and intensity
     * @param {number} color - Hex color
     * @param {number} intensity - Light intensity
     */
    setDirectional(color, intensity) {
        this.directionalColor = color;
        this.directionalIntensity = intensity;
        if (this.directionalLight) {
            this.directionalLight.color.setHex(color);
            this.directionalLight.intensity = intensity;
        }
    }

    /**
     * Update headlight visibility based on ambient light
     * Headlights turn on at night (low ambient)
     */
    updateHeadlightVisibility() {
        if (!this.headlightsEnabled) {
            if (this.headlightLeft) this.headlightLeft.visible = false;
            if (this.headlightRight) this.headlightRight.visible = false;
            return;
        }

        // Check if it's night (low ambient light)
        const isNight = this.ambientIntensity < this.headlightThreshold;
        const shouldShow = !this.headlightAutoNightOnly || isNight;

        if (this.headlightLeft) this.headlightLeft.visible = shouldShow;
        if (this.headlightRight) this.headlightRight.visible = shouldShow;
    }

    /**
     * Get current ambient light intensity
     * Used by other systems to determine day/night state
     * @returns {number} Current ambient intensity
     */
    getAmbientIntensity() {
        return this.ambientIntensity;
    }

    /**
     * Clean up all lights
     */
    dispose() {
        if (this.ambientLight) {
            this.scene.remove(this.ambientLight);
            this.ambientLight = null;
        }
        if (this.directionalLight) {
            this.scene.remove(this.directionalLight);
            this.directionalLight = null;
        }
        if (this.headlightLeft) {
            this.camera.remove(this.headlightLeft);
            this.camera.remove(this.headlightLeft.target);
            this.headlightLeft = null;
        }
        if (this.headlightRight) {
            this.camera.remove(this.headlightRight);
            this.camera.remove(this.headlightRight.target);
            this.headlightRight = null;
        }
    }
}
